import React, { useState, useEffect, useCallback } from 'react';
import { placePayOnDeliveryOrder, type PaymentMethod } from '@/services/payOnDelivery';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Heart, Share2, ShoppingCart, User, Star, Package, Truck, Shield, Plus } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ProductMetaTags } from '@/components/ProductMetaTags';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { WhatsAppShareDialog } from '@/components/share/WhatsAppShareDialog';
import { useWhatsAppShare } from '@/hooks/useWhatsAppShare';
import { PageHeader } from "@/components/shared/PageHeader";
import { PullToRefresh } from "@/components/shared/PullToRefresh";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { SimilarProducts } from '@/components/products/ProductRecommendations';
import { BuyNowModal } from '@/components/checkout/BuyNowModal';
import { AutoPlayVideo } from '@/components/products/AutoPlayVideo';


interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  images: string[];
  category: string;
  stock: number;
  status: string;
  views: number;
  likes: number;
  seller_id: string;
  created_at: string;
  video_url?: string;
}
interface SellerInfo {
  business_name: string;
  business_description: string;
  verified: boolean;
  rating: number;
  total_sales: number;
}
export default function ProductDetail() {
  const {
    productId
  } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [seller, setSeller] = useState<SellerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [liked, setLiked] = useState(false);
  const [isGeneratingPayment, setIsGeneratingPayment] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const [sellerState, setSellerState] = useState('Lagos');
  const { share, isDialogOpen, setIsDialogOpen, shareData, createShareableLink } = useWhatsAppShare();

  // Generate a session ID for view tracking (anonymous users)
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem('product_view_session');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('product_view_session', sessionId);
    }
    return sessionId;
  };
  useEffect(() => {
    if (productId) {
      fetchProduct();
    }
  }, [productId]);
  const fetchProduct = useCallback(async () => {
    try {
      if (!productId) {
        console.error('No productId in URL params');
        setLoading(false);
        return;
      }

      // Use secure RPC to avoid exposing seller_id to public users
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_public_product_by_id', {
        p_product_id: productId
      });

      if (rpcError) {
        console.error('RPC error fetching product:', rpcError, 'productId:', productId);
        throw rpcError;
      }
      const productData = rpcData?.[0];
      if (!productData) {
        console.warn('Product not found for id:', productId);
        setLoading(false);
        return;
      }

      // Fetch seller_id from products table (needed for delivery rate calculation & checkout)
      let sellerId = '';
      try {
        const { data: prodRow } = await supabase
          .from('products')
          .select('seller_id')
          .eq('id', productData.id)
          .single();
        if (prodRow?.seller_id) {
          sellerId = prodRow.seller_id;
        }
      } catch (e) {
        console.warn('Could not fetch seller_id for product:', e);
      }

      setProduct({
        id: productData.id,
        title: productData.title,
        description: productData.description || '',
        price: productData.price,
        currency: productData.currency || 'NGN',
        images: productData.images || [],
        category: productData.category || '',
        stock: productData.stock || 0,
        status: 'active',
        views: productData.views || 0,
        likes: productData.likes || 0,
        seller_id: sellerId,
        created_at: productData.created_at,
        video_url: productData.video_url || undefined,
      });

      // Set seller info from the same RPC result
      if (productData.business_name) {
        setSeller({
          business_name: productData.business_name,
          business_description: productData.business_description || '',
          verified: productData.seller_verified || false,
          rating: productData.seller_rating || 0,
          total_sales: productData.seller_total_sales || 0,
        });
        if (productData.seller_pickup_state) {
          setSellerState(productData.seller_pickup_state);
        }
      }

      // SECURITY: Use secure RPC function for view tracking with rate limiting
      const sessionId = getSessionId();
      await supabase.rpc('increment_product_view', { 
        p_product_id: productId,
        p_session_id: sessionId
      });

      // Check if user has liked this product
      const { data: likeData } = await supabase.rpc('get_user_product_like', {
        p_product_id: productId
      });
      setLiked(likeData === true);
    } catch (error) {
      console.error('Error fetching product:', error, 'productId:', productId);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const { isRefreshing, pullDistance, triggerRefresh } = usePullToRefresh({
    onRefresh: fetchProduct,
  });
  const handleAddToCart = () => {
    if (!product) return;
    
    const cartItem = {
      id: product.id,
      title: product.title,
      price: product.price,
      image: product.images?.[0],
      sellerId: product.seller_id
    };
    
    addToCart(cartItem);
    
    toast.success('Added to cart!');
  };

  const handleBuyNowClick = () => {
    if (!product) return;
    setShowBuyNowModal(true);
  };

  const handleBuyNowConfirm = async (deliveryData: {
    deliveryState: string;
    deliveryCity: string;
    deliveryAddress: string;
    recipientName: string;
    recipientPhone: string;
    deliveryFee: number;
    deliveryType: string;
    buyerEmail?: string;
    paymentMethod?: PaymentMethod;
  }) => {
    if (!product) return;
    setIsGeneratingPayment(true);
    if (deliveryData.paymentMethod === 'pay_on_delivery') {
      try {
        const result = await placePayOnDeliveryOrder({
          items: [{ productId: product.id, quantity: 1 }],
          delivery_fee: deliveryData.deliveryFee,
          delivery_type: deliveryData.deliveryType === 'fez' ? 'fez' : 'self',
          recipient_name: deliveryData.recipientName.trim(),
          recipient_phone: deliveryData.recipientPhone.trim(),
          delivery_address_line: deliveryData.deliveryAddress.trim(),
          delivery_city: deliveryData.deliveryCity,
          delivery_state: deliveryData.deliveryState,
          email: deliveryData.buyerEmail,
        });
        setShowBuyNowModal(false);
        navigate(`/order-placed?orderId=${result.orderId}&total=${Math.round(result.total)}`);
      } catch (err: any) {
        toast.error(err?.message || 'Could not place your order. Please try again.');
      } finally {
        setIsGeneratingPayment(false);
      }
      return;
    }
    try {
      // Generate payment link with delivery details
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-payment-link', {
        body: {
          productId: product.id,
          quantity: 1,
          deliveryFee: deliveryData.deliveryFee,
          deliveryType: deliveryData.deliveryType,
          deliveryAddress: {
            address: deliveryData.deliveryAddress,
            addressLine1: deliveryData.deliveryAddress,
            city: deliveryData.deliveryCity,
            state: deliveryData.deliveryState,
            country: 'Nigeria',
          },
          recipientName: deliveryData.recipientName,
          recipientPhone: deliveryData.recipientPhone,
        }
      });
      if (error) throw error;
      if (data?.paymentLink) {
        // SECURITY FIX: Use sessionStorage instead of localStorage for temporary order data
        if (data.orderId) {
          sessionStorage.setItem('pending_order_id', data.orderId);
        }
        if (data.trackingNumber) {
          sessionStorage.setItem('pending_tracking_number', data.trackingNumber);
        }
        if (data.reference) {
          sessionStorage.setItem('pending_reference', data.reference);
        }

        // Redirect to payment page
        window.location.href = data.paymentLink;
      } else {
        throw new Error('No payment link received');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast.error('Failed to generate payment link. Please try again.');
    } finally {
      setIsGeneratingPayment(false);
      setShowBuyNowModal(false);
    }
  };
  const handleShare = async () => {
    if (!product) return;
    
    const shareableLink = createShareableLink(product.id);
    
    await share({
      title: product.title,
      description: product.description,
      price: product.price,
      imageUrl: product.images?.[0],
      shareableLink,
      businessName: seller?.business_name,
    });
  };
  const handleLike = async () => {
    if (!product || isLikeLoading) return;
    
    setIsLikeLoading(true);
    try {
      // SECURITY: Use secure RPC function for like toggling (requires authentication)
      const { data, error } = await supabase.rpc('toggle_product_like', {
        p_product_id: product.id
      });
      
      if (error) {
        if (error.message.includes('Authentication required')) {
          toast.error('Please sign in to like products');
          return;
        }
        throw error;
      }
      
      if (data && typeof data === 'object' && 'count' in data && 'liked' in data) {
        const result = data as { count: number; liked: boolean };
        setProduct({
          ...product,
          likes: result.count
        });
        setLiked(result.liked);
        toast.success(result.liked ? 'Added to favorites' : 'Removed from favorites');
      }
    } catch (error) {
      console.error('Error updating likes:', error);
      toast.error('Failed to update favorites');
    } finally {
      setIsLikeLoading(false);
    }
  };
  if (loading) {
    return <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square bg-muted rounded"></div>
            <div className="space-y-4">
              <div className="h-8 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-6 bg-muted rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>;
  }
  if (!product) {
    return <div className="container mx-auto px-4 py-16 text-center">
        <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Product Not Found</h1>
        <p className="text-muted-foreground mb-6">This product may have been removed or is no longer available.</p>
        <Button onClick={() => navigate('/products')}>
          Browse Products
        </Button>
      </div>;
  }
  return <div className="container mx-auto px-4 py-8">
      <PullToRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <ProductMetaTags product={product} seller={seller} />
      <Breadcrumbs />
      
      {/* WhatsApp Share Dialog */}
      {shareData && (
        <WhatsAppShareDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          shareData={shareData}
        />
      )}
      {/* Page Header with Back Button */}
      <PageHeader
        title={product.title}
        showBack={true}
        onRefresh={triggerRefresh}
        isRefreshing={isRefreshing}
      />

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {/* Product Images and Video */}
        <div className="space-y-4">
          {/* Product Video - Auto-plays on scroll */}
          {product.video_url && (
            <AutoPlayVideo
              src={product.video_url}
              poster={product.images?.[0]}
              aspectRatio="square"
            />
          )}
          
          {/* Main Product Image */}
          <div className="aspect-square bg-muted rounded-lg overflow-hidden">
            {product.images?.[currentImageIndex] ? <img src={product.images[currentImageIndex]} alt={product.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center">
                <Package className="h-16 w-16 text-muted-foreground" />
              </div>}
          </div>
          
          {/* Image Thumbnails */}
          {product.images && product.images.length > 1 && <div className="flex gap-2 overflow-x-auto">
              {product.images.map((image, index) => <button key={index} aria-label={`View product image ${index + 1}`} onClick={() => setCurrentImageIndex(index)} className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden ${index === currentImageIndex ? 'border-primary' : 'border-muted'}`}>
                  <img src={image} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover" />
                </button>)}
            </div>}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <div className="flex items-start justify-between mb-2">
              <h1 className="text-3xl font-bold">{product.title}</h1>
              <Button variant="ghost" size="sm" onClick={handleLike} aria-label="Add to favorites" className={liked ? 'text-red-500' : ''}>
                <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
              </Button>
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="secondary">{product.category}</Badge>
              <Badge variant={product.stock > 0 ? 'default' : 'destructive'}>
                {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
              </Badge>
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{product.views} views</span>
              <span>{product.likes} likes</span>
            </div>
          </div>

          <div>
            <span className="text-3xl font-bold text-primary">
              ₦{product.price.toLocaleString()}
            </span>
          </div>

          <Separator />

          {/* Seller Info */}
          {seller && <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Seller Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{seller.business_name}</span>
                  {seller.verified && <Badge variant="default">
                      <Shield className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-current text-yellow-500" />
                    <span>{seller.rating.toFixed(1)}</span>
                  </div>
                  <span>{seller.total_sales} sales</span>
                </div>
                
                {seller.business_description && <p className="text-sm text-muted-foreground">
                    {seller.business_description}
                  </p>}
              </CardContent>
            </Card>}

          

          {/* Description */}
          {product.description && <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-muted-foreground leading-relaxed">
                {product.description}
              </p>
            </div>}

          <Separator />

          {/* Action Buttons */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Button 
                onClick={handleAddToCart} 
                disabled={product.stock === 0} 
                variant="outline" 
                size="lg" 
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add to Cart
              </Button>
              
              <Button 
                onClick={handleBuyNowClick} 
                disabled={product.stock === 0} 
                size="lg" 
                className="w-full"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {product.stock > 0 ? 'Buy Now' : 'Out of Stock'}
              </Button>
            </div>

            {/* Buy Now Modal */}
            <BuyNowModal
              open={showBuyNowModal}
              onOpenChange={setShowBuyNowModal}
              product={product}
              sellerState={sellerState}
              onConfirm={handleBuyNowConfirm}
              isProcessing={isGeneratingPayment}
            />
            
            <Button variant="outline" onClick={handleShare} size="lg" className="w-full">
              <Share2 className="h-5 w-5 mr-2" />
              Share Product
            </Button>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Truck className="h-4 w-4" />
              <span>Fast Delivery</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>Secure Payment</span>
            </div>
          </div>

          {/* Stock Warning */}
          {product.stock > 0 && product.stock <= 5 && <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <p className="text-sm font-medium text-warning-foreground">
                ⚠️ Only {product.stock} left in stock!
              </p>
            </div>}
        </div>
      </div>

      {/* Similar Products Section */}
      <SimilarProducts productId={product.id} />
    </div>;
}